import type { ReactNode } from 'react';
import type { MessageA2uiSurface, StreamBlock, StreamStatusEntry } from '../types/chat';
import {
  applyPersistedTodosToStepEntries,
  buildInterleavedStreamBlocks,
  completeInProgressStatusEntries,
  getLatestStreamTodos,
} from '../utils/stream-status-entries';
import { StreamActivityDropdown } from './StreamActivityDropdown';

interface InterleavedStreamContentProps {
  content: string;
  entries?: StreamStatusEntry[];
  surfaces?: MessageA2uiSurface[];
  streamBlocks?: StreamBlock[];
  renderText: (text: string) => ReactNode;
  renderSurfaces?: (surfaces: MessageA2uiSurface[]) => ReactNode;
  /** When false, finished Steps close once the stream ends. */
  isStreaming?: boolean;
}

function StepsBlock({
  entries,
  isStreaming,
  hasContentAfter,
}: {
  entries: StreamStatusEntry[];
  isStreaming: boolean;
  hasContentAfter: boolean;
}) {
  const resolvedEntries = isStreaming
    ? entries
    : (completeInProgressStatusEntries(entries) ?? entries);
  const waveTodos = getLatestStreamTodos(resolvedEntries);
  const stepEntries = applyPersistedTodosToStepEntries(resolvedEntries, waveTodos);
  const lastNonTodosEntry = [...stepEntries].reverse().find((entry) => !entry.todos?.length);
  const lastIcon = lastNonTodosEntry?.icon ?? stepEntries[stepEntries.length - 1]?.icon;
  const inProgress = isStreaming && (lastIcon === 'tool_started' || lastIcon === 'subagent_started');
  const shouldStayOpen = inProgress || (isStreaming && !hasContentAfter);

  return (
    <StreamActivityDropdown
      entries={stepEntries}
      active={shouldStayOpen}
      defaultOpen={shouldStayOpen}
      forceOpen={inProgress}
      shimmerActiveRow={inProgress}
    />
  );
}

function TimelineBlocks({
  blocks,
  renderText,
  renderSurfaces,
  isStreaming,
}: {
  blocks: StreamBlock[];
  renderText: (text: string) => ReactNode;
  renderSurfaces?: (surfaces: MessageA2uiSurface[]) => ReactNode;
  isStreaming: boolean;
}) {
  return (
    <>
      {blocks.map((block, index) => {
        const hasContentAfter = blocks
          .slice(index + 1)
          .some((next) =>
            next.type === 'text'
              ? next.text.trim().length > 0
              : next.type === 'steps'
                ? next.entries.length > 0
                : true
          );

        if (block.type === 'text') {
          return block.text ? <div key={`text-${index}`}>{renderText(block.text)}</div> : null;
        }

        if (block.type === 'steps') {
          return (
            <StepsBlock
              key={`steps-${index}`}
              entries={block.entries}
              isStreaming={isStreaming}
              hasContentAfter={hasContentAfter}
            />
          );
        }

        if (!renderSurfaces) return null;
        return (
          <div key={`${block.type}-${block.surface.surfaceId}`}>
            {renderSurfaces([block.surface])}
          </div>
        );
      })}
    </>
  );
}

function LegacyStepsAndText({
  content,
  entries,
  renderText,
  isStreaming = false,
  hasExternalContentAfter = false,
}: {
  content: string;
  entries?: StreamStatusEntry[];
  renderText: (text: string) => ReactNode;
  isStreaming?: boolean;
  hasExternalContentAfter?: boolean;
}) {
  const blocks = buildInterleavedStreamBlocks(content, entries);

  if (!blocks.length) {
    return content ? <>{renderText(content)}</> : null;
  }

  return (
    <>
      {blocks.map((block, index) => {
        if (block.type === 'text') {
          return <div key={`text-${index}`}>{renderText(block.text)}</div>;
        }

        const hasContentAfter = blocks
          .slice(index + 1)
          .some(
            (next) =>
              (next.type === 'text' && next.text.trim().length > 0) || next.type === 'status'
          );

        return (
          <StepsBlock
            key={`steps-${block.offset}`}
            entries={block.entries}
            isStreaming={isStreaming}
            hasContentAfter={hasContentAfter || hasExternalContentAfter}
          />
        );
      })}
    </>
  );
}

/** Socket-ordered timeline when streamBlocks exist; otherwise history fallback. */
export function InterleavedStreamContent({
  content,
  entries,
  surfaces,
  streamBlocks,
  renderText,
  renderSurfaces,
  isStreaming = false,
}: Readonly<InterleavedStreamContentProps>) {
  if (streamBlocks?.length) {
    return (
      <div className="flex min-w-0 flex-col gap-1">
        <TimelineBlocks
          blocks={streamBlocks}
          renderText={renderText}
          renderSurfaces={renderSurfaces}
          isStreaming={isStreaming}
        />
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <LegacyStepsAndText
        content={content}
        entries={entries}
        renderText={renderText}
        isStreaming={isStreaming}
        hasExternalContentAfter={Boolean(surfaces?.length && renderSurfaces)}
      />
      {surfaces?.length && renderSurfaces ? renderSurfaces(surfaces) : null}
    </div>
  );
}
